import type { CostChange } from "@cloudserver/contracts";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useCostPreview } from "../../api/costs.js";
import { Alert } from "../../ui/primitives.js";
import { useMoney } from "./shared.js";

/**
 * The cost of a change before it is made (docs/costs.md), and what it does
 * to the budget (docs/budgets.md): in hard mode a project owner is told the
 * change will be refused and may ask for a raise; administrators and soft
 * budgets are warned. Nothing shows while `change` is null (form incomplete).
 */
export function ChangeCost({
  projectId,
  change,
}: {
  projectId: string;
  change: CostChange | null;
}) {
  const { t } = useTranslation();
  const preview = useCostPreview(projectId, change);
  const fmt = useMoney(preview.data?.currency);
  if (!preview.data) return null;
  const p = preview.data;
  const adds = Number(p.toMonthEnd) > 0;
  return (
    <div className="flex flex-col gap-2">
      {adds && (
        <p className="text-xs text-muted">
          {t("budgets.changeAdds", { amount: fmt(p.toMonthEnd), monthly: fmt(p.monthly) })}
        </p>
      )}
      {p.budget?.exceeds &&
        (p.budget.blocked ? (
          <Alert tone="danger">
            <p>
              {t(
                p.budget.rule === "new"
                  ? "budgets.willBeRefusedNew"
                  : "budgets.willBeRefusedChange",
                {
                  over: fmt(p.budget.over),
                  budget: fmt(p.budget.budget),
                  limit: fmt(p.budget.limit),
                  margin: p.budget.changeMargin,
                },
              )}
            </p>
            <Link
              to="/projects/$projectId/costs"
              params={{ projectId }}
              search={{ raise: true }}
              className="mt-1 inline-flex min-h-6 items-center font-medium underline underline-offset-2"
            >
              {t("budgets.requestRaise")}
            </Link>
          </Alert>
        ) : (
          <Alert tone="warn">
            {t(p.budget.rule === "new" ? "budgets.overAsAdminNew" : "budgets.overAsAdminChange", {
              over: fmt(p.budget.over),
              budget: fmt(p.budget.budget),
              limit: fmt(p.budget.limit),
            })}
          </Alert>
        ))}
    </div>
  );
}
